import { getCoreUtils } from "../../../Admin/kReusableCoreTS/kCoreHandler";
import { kHTMLCore } from "../../../Admin/kReusableCoreTS/kHTMLCore";
import { AppIcon, AppIcons } from "./AppIcons";
import { AppChatSettingRow, AppChatSettingsRules } from "./AppChatSettingsRules";

// AppChatSettingsView - THE MARKUP of the "Chat settings" dialog (owner, 2026-10-13) and of the one menu item that opens
// it. Pure strings (the Bridge suite imports it); the browser half is `AppChatSettingsDialog`.
//
//   Chat settings · <the contact>                                   [close]
//   This chat in WhatsApp     Archive · Notifications · Disappearing messages
//   In WACommander            Star
//   Translation               the translation settings (drawn by `AppTranslateView.Panel` into `TrPanelId`), only where
//                             `/translate` says translation is available - else the section stays hidden.
//
// ⭐ THE MARKUP IS BUILT ONCE, IN THE LOADING LOOK; `AppChatSettingsScreen.Paint` then changes words and attributes IN
// PLACE. ⛔ No control is ever rebuilt while the dialog is open (a control replaced under the pointer gets no click -
// the 2026-10-12 audit's P0), and a live region is written only when its words change.
// ⛔ NEVER A WORD FOR "NOT KNOWN": a row starts as a skeleton + "Checking with WhatsApp…" with its control NOT DRAWN
// (a switch that reads "off" before the answer would be a value nobody read).
// ⛔ Every word that is data is escaped; no inline style (the CSP); icons are `AppIcons`, never typed glyphs.

export class AppChatSettingsView {
    /** On a control that opens the dialog for the OPEN chat; its value is the section to show ("" or `TrSection`). */
    public static readonly OpenAttr: string = "data-wac-chatset";
    public static readonly TrSection: string = "translation";
    public static readonly SetAttr: string = "data-cs-set";
    public static readonly ValueAttr: string = "data-cs-value";
    public static readonly RowAttr: string = "data-cs-row";
    public static readonly CloseAttr: string = "data-cs-close";
    public static readonly RetryAttr: string = "data-cs-retry";
    public static readonly DialogClass: string = "chatset";
    public static readonly RootId: string = "wac-cs";
    public static readonly TitleId: string = "wac-cs-title";
    public static readonly FailId: string = "wac-cs-fail";
    public static readonly FailWhyId: string = "wac-cs-failwhy";
    public static readonly RetryId: string = "wac-cs-retry";
    public static readonly WaId: string = "wac-cs-wa";
    public static readonly LocalId: string = "wac-cs-local";
    public static readonly TrId: string = "wac-cs-tr";
    public static readonly TrLineId: string = "wac-cs-trline";
    public static readonly TrPanelId: string = "wac-cs-trpanel";
    public static readonly MenuItemId: string = "wac-chatset-open";
    /** ⭐ CHAT AND CONTACT ACTIONS: an action's first press opens its confirm step (`AskAttr`); Cancel closes it. */
    public static readonly AskAttr: string = "data-cs-ask";
    public static readonly CancelAttr: string = "data-cs-cancel";
    public static readonly InfoId: string = "wac-cs-info";
    public static readonly InfoNoteId: string = "wac-cs-infonote";
    public static readonly LockId: string = "wac-cs-lock";
    public static readonly MenuWords: string = "Chat settings";
    /** ⭐ TABS (owner 2026-10-26): Chat / Contact / Clear & delete / Translation; `TabAttr` on each tab button. */
    public static readonly TabAttr: string = "data-cs-tab";
    public static readonly ChatTab: string = "chat";
    public static readonly ContactTab: string = "contact";
    public static readonly GoneTab: string = "gone";
    public static readonly PersonId: string = "wac-cs-person";
    public static readonly GoneId: string = "wac-cs-gone";

    /** DOCUMENTED EXCEPTION: one id rule for the view and the screen. ERROR-SUPPRESSED-JUSTIFIED: a string join. */
    public static Id = (Setting: string, Part: string): string => {
        return "wac-cs-" + Setting + "-" + Part;
    };

    /** The chat header's "⋯" menu item. ⛔ It carries no state - so it can never show one that is not known. */
    public static MenuItem = (On: boolean): string => {
        if (getCoreUtils().KErrorMode === true) { return ""; }
        try {
            if (On !== true) { return ""; }
            return "<button type=\"button\" class=\"menuitem\" role=\"menuitem\" id=\"" + AppChatSettingsView.MenuItemId + "\" "
                + AppChatSettingsView.OpenAttr + "=\"\" aria-haspopup=\"dialog\">" + AppIcons.Svg(AppIcons.Settings, 16)
                + "<span>" + AppChatSettingsView.MenuWords + "</span></button>";
        } catch (e) {
            getCoreUtils().KGlobalErrorHandler(e, "AppChatSettingsView.MenuItem");
            return "";
        }
    };

    /** The dialog's inside. `Settings` = the rows offered (a group: the star only); `Translation` = the section exists. */
    public static Body = (Title: string, Settings: string[], Translation: boolean): string => {
        if (getCoreUtils().KErrorMode === true) { return ""; }
        try {
            const Native: string[] = [];
            const Local: string[] = [];
            const Person: string[] = [];
            const Gone: string[] = [];
            for (const Setting of Settings) {
                if (Setting === AppChatSettingsRules.Star) { Local.push(AppChatSettingsView.Row(Setting)); continue; }
                if (Setting === AppChatSettingsRules.Block) { Person.push(AppChatSettingsView.Row(Setting)); continue; }
                if (Setting === AppChatSettingsRules.Clear || Setting === AppChatSettingsRules.Delete) { Gone.push(AppChatSettingsView.Row(Setting)); continue; }
                Native.push(AppChatSettingsView.Row(Setting));
            }
            const Tabs: string[][] = [];
            let Chat: string = "";
            if (Native.length > 0) { Chat += AppChatSettingsView.Section(AppChatSettingsView.WaId, "This chat in WhatsApp", Native.join("")); }
            Chat += AppChatSettingsView.Section(AppChatSettingsView.LocalId, "In WACommander", Local.join(""));
            Tabs.push([AppChatSettingsView.ChatTab, "Chat", Chat]);
            if (Person.length > 0) {
                Tabs.push([AppChatSettingsView.ContactTab, "Contact", AppChatSettingsView.Lock(Settings)
                    + AppChatSettingsView.Section(AppChatSettingsView.PersonId, "Contact in WhatsApp", Person.join("")) + AppChatSettingsView.Info(Settings)]);
            }
            if (Gone.length > 0) { Tabs.push([AppChatSettingsView.GoneTab, "Clear & delete", AppChatSettingsView.Section(AppChatSettingsView.GoneId, "On your phone", Gone.join(""))]); }
            if (Translation === true) { Tabs.push([AppChatSettingsView.TrSection, "Translation", AppChatSettingsView.Translation(Translation)]); }
            return "<div class=\"cs\" id=\"" + AppChatSettingsView.RootId + "\">" + AppChatSettingsView.Head(Title)
                + "<div class=\"cs-body\">" + AppChatSettingsView.Fail() + AppChatSettingsView.TabList(Tabs) + "</div></div>";
        } catch (e) {
            getCoreUtils().KGlobalErrorHandler(e, "AppChatSettingsView.Body");
            return "";
        }
    };

    /** The tab row + one panel per tab; the first is shown. A single tab draws no row. Tabs: [key, label, panel markup]. */
    private static TabList = (Tabs: string[][]): string => {
        if (getCoreUtils().KErrorMode === true) { return ""; }
        try {
            const Row: string[] = [];
            const Panels: string[] = [];
            Tabs.forEach((Tab: string[], At: number): void => {
                const On: boolean = At === 0;
                const TabId: string = AppChatSettingsView.TabId(Tab[0]);
                Row.push("<button type=\"button\" class=\"cs-tab\" role=\"tab\" id=\"" + TabId + "\" " + AppChatSettingsView.TabAttr + "=\"" + Tab[0]
                    + "\" aria-controls=\"" + AppChatSettingsView.PanelId(Tab[0]) + "\" aria-selected=\"" + String(On) + "\" tabindex=\"" + (On ? "0" : "-1")
                    + "\">" + kHTMLCore.EscapeHtml(Tab[1]) + "</button>");
                Panels.push("<div class=\"cs-panel\" role=\"tabpanel\" id=\"" + AppChatSettingsView.PanelId(Tab[0]) + "\" aria-labelledby=\"" + TabId + "\""
                    + (On ? "" : " hidden") + ">" + Tab[2] + "</div>");
            });
            let Head: string = "";
            if (Tabs.length > 1) { Head = "<div class=\"cs-tabs\" role=\"tablist\" aria-label=\"Chat settings\">" + Row.join("") + "</div>"; }
            return Head + Panels.join("");
        } catch (e) {
            getCoreUtils().KGlobalErrorHandler(e, "AppChatSettingsView.TabList");
            return "";
        }
    };

    /** DOCUMENTED EXCEPTION: id rules for a tab and its panel. ERROR-SUPPRESSED-JUSTIFIED: a string join. */
    public static TabId = (Key: string): string => { return "wac-cs-tab-" + Key; };
    public static PanelId = (Key: string): string => { return "wac-cs-panel-" + Key; };

    private static Head = (Title: string): string => {
        if (getCoreUtils().KErrorMode === true) { return ""; }
        try {
            return "<header class=\"cs-head\"><div class=\"cs-headtext\"><h2 id=\"" + AppChatSettingsView.TitleId + "\">Chat settings</h2>"
                + "<p class=\"cs-who\">" + kHTMLCore.EscapeHtml(Title) + "</p></div><button type=\"button\" class=\"btn ghost icon cs-close\" "
                + AppChatSettingsView.CloseAttr + "=\"\" aria-label=\"Close chat settings\" title=\"Close\">" + AppIcons.Svg(AppIcons.Close, 18)
                + "</button></header>";
        } catch (e) {
            getCoreUtils().KGlobalErrorHandler(e, "AppChatSettingsView.Head");
            return "";
        }
    };

    /** The one sentence shown INSTEAD of the rows when the settings could not be read, with "Try again". Hidden at first. */
    private static Fail = (): string => {
        if (getCoreUtils().KErrorMode === true) { return ""; }
        try {
            return "<div class=\"cs-fail\" id=\"" + AppChatSettingsView.FailId + "\" role=\"status\" hidden><p id=\""
                + AppChatSettingsView.FailWhyId + "\"></p><button type=\"button\" class=\"btn secondary sm\" id=\"" + AppChatSettingsView.RetryId
                + "\" " + AppChatSettingsView.RetryAttr + "=\"\">Try again</button></div>";
        } catch (e) {
            getCoreUtils().KGlobalErrorHandler(e, "AppChatSettingsView.Fail");
            return "";
        }
    };

    /** A titled group of rows; "" when it has none (a group chat has no WhatsApp rows). */
    private static Section = (Id: string, Heading: string, Rows: string): string => {
        if (getCoreUtils().KErrorMode === true) { return ""; }
        try {
            if (Rows.length === 0) { return ""; }
            return "<section class=\"cs-sec\" id=\"" + Id + "\" aria-labelledby=\"" + Id + "-h\"><h3 id=\"" + Id + "-h\">"
                + kHTMLCore.EscapeHtml(Heading) + "</h3>" + Rows + "</section>";
        } catch (e) {
            getCoreUtils().KGlobalErrorHandler(e, "AppChatSettingsView.Section");
            return "";
        }
    };

    /** One line, hidden until the service says the chat is locked on the phone. Nothing is hidden because of it. */
    private static Lock = (Settings: string[]): string => {
        if (getCoreUtils().KErrorMode === true) { return ""; }
        try {
            if (Settings.indexOf(AppChatSettingsRules.Pin) < 0) { return ""; }
            return "<p class=\"cs-lock\" id=\"" + AppChatSettingsView.LockId + "\" hidden>" + AppIcons.Svg(AppIcons.Lock, 16)
                + "<span>This chat is locked in WhatsApp on your phone.</span></p>";
        } catch (e) {
            getCoreUtils().KGlobalErrorHandler(e, "AppChatSettingsView.Lock");
            return "";
        }
    };

    /**
     * "About this contact": one `div` per field, every one `hidden` until the service gives it a value - so a field is
     * never drawn without one. Built once; the screen fills the words in place.
     */
    private static Info = (Settings: string[]): string => {
        if (getCoreUtils().KErrorMode === true) { return ""; }
        try {
            if (Settings.indexOf(AppChatSettingsRules.Pin) < 0) { return ""; }
            const Id: string = AppChatSettingsView.InfoId;
            const Out: string[] = ["<section class=\"cs-sec cs-info\" id=\"" + Id + "\" aria-labelledby=\"" + Id + "-h\" hidden><h3 id=\"" + Id
                + "-h\">About</h3><dl class=\"cs-dl\">"];
            for (const Field of AppChatSettingsRules.InfoFields) {
                const Icon: AppIcon | null = Field === "address" ? AppIcons.Place : (Field === "website" ? AppIcons.Globe : (Field === "email" ? AppIcons.Mail : null));
                const Ico: string = Icon === null ? "" : "<span class=\"cs-dlico\" aria-hidden=\"true\">" + AppIcons.Svg(Icon, 16) + "</span>";
                Out.push("<div id=\"" + AppChatSettingsView.Id("info", Field) + "\" hidden>" + Ico + "<dt id=\"" + AppChatSettingsView.Id("info", Field + "-l")
                    + "\"></dt><dd id=\"" + AppChatSettingsView.Id("info", Field + "-v") + "\"></dd></div>");
            }
            Out.push("</dl><p class=\"cs-note\" id=\"" + AppChatSettingsView.InfoNoteId + "\" hidden></p></section>");
            return Out.join("");
        } catch (e) {
            getCoreUtils().KGlobalErrorHandler(e, "AppChatSettingsView.Info");
            return "";
        }
    };

    /** The translation section: hidden until `/translate` says translation is available for this chat. */
    private static Translation = (On: boolean): string => {
        if (getCoreUtils().KErrorMode === true) { return ""; }
        try {
            if (On !== true) { return ""; }
            const Id: string = AppChatSettingsView.TrId;
            return "<section class=\"cs-sec cs-tr\" id=\"" + Id + "\" aria-labelledby=\"" + Id + "-h\" hidden><h3 id=\"" + Id
                + "-h\">Translation</h3><p class=\"trline\" id=\"" + AppChatSettingsView.TrLineId + "\" role=\"status\" hidden></p>"
                + "<div class=\"cs-trpanel\" id=\"" + AppChatSettingsView.TrPanelId + "\" hidden></div></section>";
        } catch (e) {
            getCoreUtils().KGlobalErrorHandler(e, "AppChatSettingsView.Translation");
            return "";
        }
    };

    /**
     * Review 2026-11-01 ("far too much text"): a row shows its name and value only. The reason a control is off
     * (`why`) is a TOOLTIP on this info icon and on the control, and the control's description for a screen reader
     * (`.cs-why` is `.sr`). Hidden until there is a reason (`AppChatSettingsScreen.PaintRow`).
     */
    public static InfoIcon = (Setting: string): string => {
        if (getCoreUtils().KErrorMode === true) { return ""; }
        try {
            return "<span class=\"cs-info\" id=\"" + AppChatSettingsView.Id(Setting, "info") + "\" aria-hidden=\"true\" hidden>"
                + AppIcons.Svg(AppIcons.Help, 16) + "</span>";
        } catch (e) {
            getCoreUtils().KGlobalErrorHandler(e, "AppChatSettingsView.InfoIcon");
            return "";
        }
    };

    /** One setting's row in its LOADING look (`AppChatSettingsRules.LoadingRow`): name, skeleton, hint; control not drawn yet. */
    public static Row = (Setting: string): string => {
        if (getCoreUtils().KErrorMode === true) { return ""; }
        try {
            const Start: AppChatSettingRow = AppChatSettingsRules.LoadingRow(Setting);
            const NameId: string = AppChatSettingsView.Id(Setting, "name");
            return "<div class=\"cs-row\" " + AppChatSettingsView.RowAttr + "=\"" + Setting + "\" aria-busy=\"true\"><div class=\"cs-text\">"
                + "<span class=\"cs-name\" id=\"" + NameId + "\">" + kHTMLCore.EscapeHtml(Start.Name) + "</span>" + AppChatSettingsView.InfoIcon(Setting)
                + "<span class=\"cs-state\">"
                + "<span class=\"skel cs-skel\" id=\"" + AppChatSettingsView.Id(Setting, "skel") + "\" aria-hidden=\"true\"></span>"
                + "<span class=\"cs-words\" id=\"" + AppChatSettingsView.Id(Setting, "words") + "\" hidden></span>"
                + "<span class=\"cs-hint\" id=\"" + AppChatSettingsView.Id(Setting, "hint") + "\">" + kHTMLCore.EscapeHtml(Start.Hint) + "</span>"
                + "<span class=\"cs-secs\" id=\"" + AppChatSettingsView.Id(Setting, "secs") + "\" aria-hidden=\"true\"></span></span></div>"
                + "<div class=\"cs-ctl\" id=\"" + AppChatSettingsView.Id(Setting, "ctl") + "\">" + AppChatSettingsView.Control(Setting, NameId)
                + "</div><p class=\"cs-note\" id=\"" + AppChatSettingsView.Id(Setting, "note") + "\" role=\"status\" hidden></p>"
                + "<p class=\"cs-why sr\" id=\"" + AppChatSettingsView.Id(Setting, "why") + "\" hidden></p></div>";
        } catch (e) {
            getCoreUtils().KGlobalErrorHandler(e, "AppChatSettingsView.Row");
            return "";
        }
    };

    /**
     * An action (clear, delete): one button that opens its confirm step - the plain sentence, the real button
     * (`SetAttr` + value "true") and Cancel. ⛔ The first press sends nothing. Both parts are built once; only `hidden` moves.
     */
    private static Action = (Setting: string, NameId: string, WhyId: string): string => {
        if (getCoreUtils().KErrorMode === true) { return ""; }
        try {
            const Act: string = kHTMLCore.EscapeHtml(AppChatSettingsRules.ActWords[Setting] || "");
            const AskId: string = AppChatSettingsView.Id(Setting, "ask");
            return "<button type=\"button\" class=\"btn secondary sm cs-ask\" id=\"" + AskId + "\" " + AppChatSettingsView.AskAttr + "=\"" + Setting
                + "\" aria-labelledby=\"" + NameId + "\" aria-describedby=\"" + WhyId + "\" aria-expanded=\"false\" aria-controls=\""
                + AppChatSettingsView.Id(Setting, "confirm") + "\" hidden aria-disabled=\"true\">" + Act + "&#8230;</button>"
                + "<div class=\"cs-confirm\" id=\"" + AppChatSettingsView.Id(Setting, "confirm") + "\" role=\"group\" aria-labelledby=\""
                + AppChatSettingsView.Id(Setting, "confirmtext") + "\" hidden><p id=\"" + AppChatSettingsView.Id(Setting, "confirmtext") + "\">"
                + kHTMLCore.EscapeHtml(AppChatSettingsRules.ConfirmWords[Setting] || "") + "</p><div class=\"cs-confirmacts\">"
                + "<button type=\"button\" class=\"btn danger sm\" id=\"" + AppChatSettingsView.Id(Setting, "go") + "\" " + AppChatSettingsView.SetAttr
                + "=\"" + Setting + "\" " + AppChatSettingsView.ValueAttr + "=\"true\">" + Act + "</button>"
                + "<button type=\"button\" class=\"btn ghost sm\" id=\"" + AppChatSettingsView.Id(Setting, "cancel") + "\" "
                + AppChatSettingsView.CancelAttr + "=\"" + Setting + "\">Cancel</button></div></div>";
        } catch (e) {
            getCoreUtils().KGlobalErrorHandler(e, "AppChatSettingsView.Action");
            return "";
        }
    };

    /** The control, `hidden` until the first answer: a switch (archive, star) or one button per choice (mute, timer). */
    private static Control = (Setting: string, NameId: string): string => {
        if (getCoreUtils().KErrorMode === true) { return ""; }
        try {
            const WhyId: string = AppChatSettingsView.Id(Setting, "why");
            if (AppChatSettingsRules.IsAction(Setting) === true) { return AppChatSettingsView.Action(Setting, NameId, WhyId); }
            if (AppChatSettingsRules.IsSwitch(Setting) === true) {
                return "<button type=\"button\" class=\"cs-switch\" id=\"" + AppChatSettingsView.Id(Setting, "switch") + "\" role=\"switch\""
                    + " aria-checked=\"false\" aria-labelledby=\"" + NameId + "\" aria-describedby=\"" + WhyId + "\" "
                    + AppChatSettingsView.SetAttr + "=\"" + Setting + "\" hidden aria-disabled=\"true\"><span class=\"cs-thumb\" aria-hidden=\"true\"></span></button>";
            }
            const Out: string[] = ["<div class=\"cs-seg\" id=\"" + AppChatSettingsView.Id(Setting, "seg") + "\" role=\"group\" aria-labelledby=\""
                + NameId + "\" aria-describedby=\"" + WhyId + "\" hidden>"];
            for (const One of AppChatSettingsRules.Options(Setting)) {
                Out.push("<button type=\"button\" class=\"btn secondary sm cs-opt\" " + AppChatSettingsView.SetAttr + "=\"" + Setting + "\" "
                    + AppChatSettingsView.ValueAttr + "=\"" + kHTMLCore.EscapeAttr(One[0]) + "\" aria-pressed=\"false\" aria-disabled=\"true\">"
                    + kHTMLCore.EscapeHtml(One[1]) + "</button>");
            }
            Out.push("</div>");
            return Out.join("");
        } catch (e) {
            getCoreUtils().KGlobalErrorHandler(e, "AppChatSettingsView.Control");
            return "";
        }
    };
}
